/**
 * Right-hand side of Zudoku's top header, injected via the
 * `head-navigation-end` slot. Renders:
 *
 *   [GitHub pill]  [Zudoku theme toggle — owned by Zudoku]  [|]  [Supported by pill]
 *
 * Zudoku always renders its own theme toggle at the end of the header row,
 * AFTER the content we put in this slot. To get the visual order above,
 * we rely on flex `order` values — set on our own elements here, and on
 * the toggle button via customCss (see `src/styles/custom-source.css`).
 */
import { PostHogInit } from "./PostHogInit";
import SiteMeta from "./SiteMeta";

const GithubIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
  </svg>
);

/**
 * Real Zuplo logo from https://zuplo.com/logo-zuplo.svg. Rendered via
 * CSS mask-image so the fill inherits from the surrounding text colour.
 * That way a single asset works in both light + dark header variants.
 */
const ZuploWordmark = () => (
  <span
    role="img"
    aria-label="Zuplo"
    className="akg-zuplo-mark"
  />
);

export function HeaderActions() {
  const posthogKey =
    (typeof import.meta !== "undefined" && (import.meta as any).env?.ZUDOKU_PUBLIC_POSTHOG_KEY) ||
    undefined;

  return (
    <>
      <SiteMeta />
      <a
        href="https://github.com/zuplo/apikeys.guide"
        target="_blank"
        rel="noopener noreferrer"
        className="akg-pill akg-pill--github"
        data-akg-order="github"
      >
        <GithubIcon />
        <span>GitHub</span>
      </a>
      <span className="akg-header-separator" data-akg-order="separator" aria-hidden="true" />
      <a
        href="https://zuplo.com?ref=apikeys-guide&utm_source=apikeys-guide&utm_medium=web&utm_campaign=api-keys"
        target="_blank"
        rel="noopener noreferrer"
        className="akg-pill akg-pill--sponsor"
        data-akg-order="sponsor"
        aria-label="Supported by Zuplo"
      >
        <span className="akg-pill__label">Supported by</span>
        <ZuploWordmark />
      </a>
      {posthogKey ? <PostHogInit apiKey={posthogKey} /> : null}
    </>
  );
}

export default HeaderActions;
